import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

import { useMemoryLineage } from "../../api/memoryApi"
import type { MemoryLineageEvent } from "../../api/types"
import type { ComplianceEventOut } from "../../api/types/masterData"
import { AuditTrail } from "../shared/AuditTrail"

/**
 * One memory event, in the shape `AuditTrail` already knows how to render.
 *
 * A PURE MAPPING AT THE API BOUNDARY, not a widened `AuditTrail` prop type. There is no
 * `entityType`/`entityId` for a memory event the way there is for a master-data row — this
 * invents the closest honest stand-in rather than asking `prettyEventLabel` /
 * `getEventIcon` / `renderDiff` to learn a second shape, each of which switches on
 * `eventType` and would need auditing for a prop type widened instead of mapped.
 *
 * `performedBy` IS NEVER RENDERED — `AuditTrail` has no render path for it (confirmed in
 * its own module). Still filled honestly rather than left empty, with the same
 * you/colleague token `MemoryTrail` already uses, in case a render path is ever added.
 */
export function toComplianceEvent(
  event: MemoryLineageEvent,
): ComplianceEventOut {
  return {
    id: event.id,
    entityType: "memory",
    entityId: event.entryId,
    eventType: event.eventType,
    oldValue: null,
    // What the entry SAID, already rendered by the backend ("0.05 %") — the one thing
    // `AuditTrail` shows for a memory event. The raw before/after JSON on the wire is
    // counters and status, not something a reader can use, so it is not forwarded.
    newValue: event.value != null ? { value: event.value } : null,
    performedBy: event.isYou ? "you" : "a colleague",
    triggeredBy: null,
    performedAt: event.performedAt,
    sourceImportId: null,
    sourceImportFilename: null,
    sourceImportUrl: null,
    sourceDocUrl: null,
    // Reuses AuditTrail's own "named, not linked" rendering (`sourceDocUrl` stays null) —
    // the same deliberate gap `MemoryTrail`'s own docstring already documents for a
    // questionnaire source: naming it beats a dead link more often than a link would help.
    sourceDocName: event.sourceRef,
    echaListVersion: null,
    notes: null,
  }
}

export interface MemoryLineageSheetProps {
  /** Null while nothing has an entry id to show yet — the sheet simply never opens for a
   *  conflict side or hint with no `entryId`, the same guard `MemoryRating` applies. */
  entryId: string | null
  surface: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The "Full history" panel — an entry's whole lineage, opened from `MemoryTrail`.
 *
 * FETCHES LAZILY, gated on `open`: `useMemoryLineage`'s own `enabled` stays false until
 * this sheet is actually opened, so a screen with many suggestions never pays for a history
 * almost nobody expands further than `because`.
 *
 * A SHARED, GENERIC SHEET (`@/components/ui/sheet`), not the review-mask-local `sv2-sheet`
 * — `MemoryTrail` is used from the supplier gate and the questionnaire page too, neither of
 * which sit inside the review mask's own scoped styling.
 */
export function MemoryLineageSheet({
  entryId,
  surface,
  open,
  onOpenChange,
}: MemoryLineageSheetProps) {
  const { translate } = useTranslation()
  const query = useMemoryLineage(entryId, surface, {}, open)
  const events = (query.data?.pages.flatMap((p) => p.items) ?? []).map(
    toComplianceEvent,
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{translate("MC_MEMORY_FULL_HISTORY")}</SheetTitle>
        </SheetHeader>
        {query.isError ? (
          // A failed fetch is not an empty history: `AuditTrail` would report "no events",
          // which reads as "nothing ever happened" after a 403, a 404 or a dropped network.
          <div
            role="alert"
            data-testid="memory-lineage-error"
            className="mt-4 flex flex-col items-start gap-2 text-sm text-grey6"
          >
            <p>{translate("MC_MEMORY_HISTORY_ERROR")}</p>
            <Button
              variant="outline"
              size="sm"
              data-testid="memory-lineage-retry"
              onClick={() => query.refetch()}
            >
              {translate("MC_MEMORY_HISTORY_RETRY")}
            </Button>
          </div>
        ) : (
          <AuditTrail
            events={events}
            isLoading={query.isLoading}
            hasMore={query.hasNextPage}
            isFetchingMore={query.isFetchingNextPage}
            onLoadMore={() => query.fetchNextPage()}
            defaultOpen
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
